// frontend/src/pages/ClientPortal.tsx
// Версия: 2.0.0 (2026-02-24)
// ИЗМЕНЕНИЯ v2.0.0:
// - localeText для обоих DataGrid (русификация "Нет данных", "Строк на странице")
// - overflow: 'hidden' на корневом Box
// - Клик по строке → Dialog с деталями заявки
// - Статус Chip вместо текста
// - pageSize 5 → 10
// - flex-колонки для устранения горизонтального скролла
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from 'react-router-dom';
import { BackendService } from '../services/BackendService';
import { useAuth } from '../contexts/AuthContext';
import { getRequestStatusLabel, getRequestStatusColor } from '../utils/statusLabels';
import { formatTimeWithDate } from '../utils/formatTestInterval';
import { buildRequestResendState } from '../utils/requestResendPrefill';

type RequestItem = {
  id: number | string;
  owner_organization?: string;

  ssas_number?: string;
  vessel_name?: string;
  imo_number?: string;
  mmsi?: string;
  planned_test_date?: string;
  test_date?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  source?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  additional_emails?: string[] | string | null;
  additionalEmails?: string[] | string | null;
  notes?: string;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;

  [key: string]: unknown;
};

type TerminalItem = {
  id?: number | string;
  ssas_number?: string;
  vessel_name?: string;
  imo_number?: string;
  mmsi?: string;
  coordinates?: any;
  received_at?: string;
  sender_email?: string;
  signal_status?: string;
  request_status?: string;
  confirmed_to?: string;

  [key: string]: unknown;
};

function formatSource(source?: string): string {
  if (!source) return '\u2014';
  if (source === 'WEB_FORM') return '\u0412\u0435\u0431-\u0444\u043e\u0440\u043c\u0430';
  return source.replace(/,/g, ' + ');
}

function formatCoordinates(coords: any): string {
  if (!coords) return '\u2014';
  const lat = coords.lat ?? coords.latitude;
  const lng = coords.lng ?? coords.longitude;
  if (lat == null || lng == null) return '\u2014';
  return `${Number(lat).toFixed(4)}\u00b0, ${Number(lng).toFixed(4)}\u00b0`;
}

function detectChannel(senderEmail?: string): string {
  if (!senderEmail) return '\u2014';
  const lower = senderEmail.toLowerCase();
  const hasMorspas = lower.includes('morspas');
  const hasMorflot = lower.includes('morflot');
  if (hasMorspas && hasMorflot) return 'MORSPAS + MORFLOT';
  if (hasMorspas) return 'MORSPAS';
  if (hasMorflot) return 'MORFLOT';
  return '\u2014';
}

function normalizeList<T extends Record<string, unknown>>(input: unknown): T[] {
  if (Array.isArray(input)) return input as T[];
  if (input && typeof input === 'object') {
    const obj = input as { success?: boolean; data?: unknown };
    if (obj.success && Array.isArray(obj.data)) {
      return obj.data as T[];
    }
    const maybeData = (input as { data?: unknown }).data;
    if (Array.isArray(maybeData)) return maybeData as T[];
  }
  return [];
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return (
      new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  } catch {
    return dateStr;
  }
}

function formatAdditionalEmails(value: unknown): string {
  if (Array.isArray(value)) {
    const emails = value.map((item) => String(item).trim()).filter(Boolean);
    return emails.length > 0 ? emails.join(', ') : '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return formatAdditionalEmails(parsed);
      } catch {
        // Fall through to rendering the original string.
      }
    }

    return trimmed;
  }

  return '—';
}

const ClientPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [terminals, setTerminals] = useState<TerminalItem[]>([]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestItem | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [reqRaw, termRaw] = await Promise.all([
          BackendService.getMyRequests(),
          BackendService.getTerminals(),
        ]);

        const allRequests = normalizeList<RequestItem>(reqRaw);
        const allTerminals = normalizeList<TerminalItem>(termRaw);

        // Добавляем id для терминалов если отсутствует (DataGrid требует уникальный id)
        const terminalsWithId = allTerminals.map((t, index) => ({
          ...t,
          id: (t.id ?? t.ssas_number ?? t.terminal_number ?? index) as string | number,
        }));

        setRequests(allRequests);
        setTerminals(terminalsWithId);
      } catch (error) {
        console.error('Ошибка загрузки данных клиента', error);
        setRequests([]);
        setTerminals([]);
      }
    };

    loadData();
  }, [user]);

  const handleNewRequest = () => {
    navigate('/client/request/new');
  };

  const handleSendAgain = (request: RequestItem) => {
    navigate('/client/request/new', {
      state: buildRequestResendState(request),
    });
  };

  const handleRowClick = (params: any) => {
    if (params?.row) {
      setSelectedRequest(params.row as RequestItem);
      setDetailsOpen(true);
    }
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRequest(null);
  };

  // ============================================================================
  // ИСПРАВЛЕНО: valueGetter для MUI DataGrid v6+
  // Новая сигнатура: (value, row, column, apiRef) => result
  // ============================================================================

  const requestColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60, flex: 0 },
    { field: 'ssas_number', headerName: 'Терминал SSAS', width: 130, flex: 0 },
    { field: 'vessel_name', headerName: 'Судно', minWidth: 140, flex: 1 },
    { field: 'imo_number', headerName: 'IMO', width: 90, flex: 0 },
    { field: 'mmsi', headerName: 'MMSI', width: 100, flex: 0 },
    {
      field: 'planned_test_date',
      headerName: 'Интервал теста (UTC)',
      width: 170,
      flex: 0,
      valueGetter: (_value: any, row: RequestItem) => {
        if (!row) return '';
        if (row.start_time || row.end_time) {
          return `${row.start_time || '?'} – ${row.end_time || '?'} UTC`;
        }
        const date = row.planned_test_date || row.test_date;
        if (!date) return '';
        return new Date(date).toLocaleDateString('ru-RU');
      },
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 130,
      flex: 0,
      renderCell: (params: any) => {
        const status = String(params?.row?.status || '');
        return (
          <Chip
            label={getRequestStatusLabel(status)}
            color={getRequestStatusColor(status)}
            size="small"
          />
        );
      },
    },
    {
      field: 'source',
      headerName: 'Источник',
      width: 130,
      flex: 0,
      renderCell: (params: any) => {
        const src = formatSource(params?.row?.source);
        return <Chip label={src} size="small" variant="outlined" />;
      },
    },
    { field: 'contact_phone', headerName: 'Телефон', minWidth: 140, flex: 1 },
    {
      field: 'send_again',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Tooltip title="Отправить снова">
          <IconButton
            size="small"
            aria-label="Отправить снова"
            onClick={(event) => {
              event.stopPropagation();
              handleSendAgain(params.row as RequestItem);
            }}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const terminalColumns: GridColDef[] = [
    { field: 'ssas_number', headerName: 'Терминал SSAS', width: 140, flex: 0 },
    { field: 'vessel_name', headerName: 'Судно', minWidth: 130, flex: 1 },
    { field: 'imo_number', headerName: 'IMO', width: 90, flex: 0 },
    { field: 'mmsi', headerName: 'MMSI', width: 100, flex: 0 },
    {
      field: 'coordinates',
      headerName: 'Координаты',
      width: 160,
      flex: 0,
      valueGetter: (_value: any, row: TerminalItem) => {
        if (!row) return '';
        return formatCoordinates(row.coordinates);
      },
    },
    {
      field: 'received_at',
      headerName: 'Получен',
      width: 180,
      flex: 0,
      renderCell: (params: any) => {
        const row = params?.row as TerminalItem;
        if (!row) return '';
        const dateStr = row.received_at ? formatDateTime(row.received_at) : '\u2014';
        const channel = detectChannel(row.sender_email);
        return (
          <Box>
            <Typography variant="body2" fontSize={13}>
              {dateStr}
            </Typography>
            {channel !== '\u2014' && (
              <Typography variant="caption" color="text.secondary">
                {channel}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'request_status',
      headerName: 'Заявка',
      width: 200,
      flex: 0,
      valueGetter: (_value: any, row: TerminalItem) => {
        if (!row) return '';
        if (row.request_status === 'confirmed' && row.confirmed_to) {
          return `Подтверждена на ${row.confirmed_to}`;
        }
        return 'Обработка';
      },
    },
    {
      field: 'signal_status',
      headerName: 'Статус',
      width: 130,
      flex: 0,
      renderCell: (params: any) => {
        const status = String(params?.row?.signal_status || '');
        return (
          <Chip
            label={getRequestStatusLabel(status)}
            color={getRequestStatusColor(status)}
            size="small"
          />
        );
      },
    },
  ];

  const localeText = {
    noRowsLabel: 'Нет данных',
    MuiTablePagination: {
      labelRowsPerPage: 'Строк на странице',
    },
  };

  return (
    <Box sx={{ p: 3, overflow: 'hidden' }}>
      <Typography variant="h4" gutterBottom>
        Портал судовладельца:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Мои заявки</Typography>
              <Button variant="contained" color="primary" onClick={handleNewRequest}>
                Новая заявка
              </Button>
            </Box>
            <DataGrid
              rows={requests}
              columns={requestColumns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              autoHeight
              onRowClick={handleRowClick}
              sx={{ cursor: 'pointer' }}
              localeText={localeText}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>
              Мои терминалы (поступили сигналы по заявкам)
            </Typography>
            <DataGrid
              rows={terminals}
              columns={terminalColumns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              autoHeight
              localeText={localeText}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f5f9ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Руководство пользователя
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Пошаговая инструкция по подаче заявок и получению подтверждений
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/client/guide')}
            startIcon={<HelpOutlineIcon />}
          >
            Открыть
          </Button>
        </Box>
      </Paper>

      {/* ========= ДИАЛОГ ДЕТАЛЕЙ ЗАЯВКИ ========= */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#1976d2',
            color: 'white',
          }}
        >
          <Typography variant="h6">Заявка #{selectedRequest?.id}</Typography>
          <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Судно:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedRequest.vessel_name || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Терминал ССТО:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#e3f2fd',
                    px: 1,
                    borderRadius: 1,
                    display: 'inline-block',
                  }}
                >
                  {selectedRequest.ssas_number || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  IMO:
                </Typography>
                <Typography variant="body1">{selectedRequest.imo_number || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  MMSI:
                </Typography>
                <Typography variant="body1">{selectedRequest.mmsi || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Интервал теста (UTC):
                </Typography>
                {selectedRequest.start_time || selectedRequest.end_time ? (
                  <Box>
                    <Typography variant="body1">
                      Начало:{' '}
                      {formatTimeWithDate(
                        selectedRequest.start_time,
                        selectedRequest.planned_test_date || selectedRequest.test_date
                      )}
                    </Typography>
                    <Typography variant="body1">
                      Окончание:{' '}
                      {formatTimeWithDate(
                        selectedRequest.end_time,
                        selectedRequest.planned_test_date || selectedRequest.test_date
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1">
                    {selectedRequest.planned_test_date || selectedRequest.test_date || '—'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Статус:
                </Typography>
                <Chip
                  label={getRequestStatusLabel(String(selectedRequest.status || ''))}
                  color={getRequestStatusColor(String(selectedRequest.status || ''))}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Email:
                </Typography>
                <Typography variant="body1">{selectedRequest.contact_email || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дополнительная адресация:
                </Typography>
                <Typography variant="body1">
                  {formatAdditionalEmails(
                    selectedRequest.additional_emails ?? selectedRequest.additionalEmails
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Телефон:
                </Typography>
                <Typography variant="body1">{selectedRequest.contact_phone || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Организация:
                </Typography>
                <Typography variant="body1">{selectedRequest.owner_organization || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дата создания:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Обновлено:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.updated_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Источник:
                </Typography>
                <Chip
                  label={formatSource(selectedRequest.source)}
                  size="small"
                  variant="outlined"
                />
              </Grid>
              {['rejected', 'failed'].includes(selectedRequest.status || '') && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    Причина отклонения:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ bgcolor: '#ffebee', p: 1, borderRadius: 1, mt: 0.5 }}
                  >
                    {selectedRequest.rejection_reason ||
                      selectedRequest.notes ||
                      'Причина не указана'}
                  </Typography>
                </Grid>
              )}
              {selectedRequest.notes &&
                !['rejected', 'failed'].includes(selectedRequest.status || '') && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Примечания:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mt: 0.5 }}
                    >
                      {selectedRequest.notes}
                    </Typography>
                  </Grid>
                )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetails} variant="contained">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPortal;
